
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'お問い合わせ',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3sshjds166brm8obq50', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'お問い合わせ',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">お問い合わせ</h2>
          <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            ご質問やご相談など、お気軽にお問い合わせください
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">お問い合わせフォーム</h3>
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">お名前 *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">メールアドレス *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">電話番号</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    placeholder="03-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-gray-700 font-semibold mb-2">お問い合わせ種別 *</label>
                  <div className="relative">
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none cursor-pointer text-sm"
                    >
                      <option value="お問い合わせ">お問い合わせ</option>
                      <option value="体験レッスン申込">体験レッスン申込</option>
                      <option value="コース詳細について">コース詳細について</option>
                      <option value="求人応募">求人応募</option>
                      <option value="その他">その他</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">お問い合わせ内容 *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none text-sm"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">{formData.message.length}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-pink-400 to-orange-400 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    送信に失敗しました。お手数ですが、もう一度お試しください。
                  </div>
                )}
              </form>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">アクセス情報</h3>
              <div className="bg-gray-50 rounded-2xl p-8 mb-6">
                <div className="space-y-6">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-orange-500 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">所在地</h4>
                      <p className="text-gray-600">〒150-0001<br />東京都渋谷区神宮前3-25-18</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-train-line text-2xl text-orange-5

000 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">アクセス</h4>
                      <p className="text-gray-600">東京メトロ表参道駅より徒歩5分<br />JR原宿駅より徒歩8分</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-phone-line text-2xl text-orange-500 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">電話番号</h4>
                      <p className="text-gray-600">03-1234-5678</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-time-line text-2xl text-orange-500 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">営業時間</h4>
                      <p className="text-gray-600">平日 10:00〜21:00<br />土日祝 9:00〜19:00<br />定休日：月曜日</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-lg h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.2479712295147!2d139.70788931525895!3d35.67148038019441!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188ca1e3a8b0e1%3A0x8e8e8e8e8e8e8e8e!2z5p2x5Lqs6YO95riL6LC35Yy656We5a6u5YmN!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="地図"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
